// packages/web/src/styles/theme.ts
import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    primary: {
      main: '#c4d600',     // Lime green - matching landing page
      light: '#d4e63c',    // Lighter lime
      dark: '#b3c200',     // Darker lime
      contrastText: '#1a1a1a',
    },
    secondary: {
      main: '#84b817',     // Green - matching landing page accent
      light: '#9ccc65',    
      dark: '#679614',     
      contrastText: '#ffffff',
    },
    success: {
      main: '#84b817',     
      light: '#9ccc65',
      dark: '#679614',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#2196f3',
    },
    background: {
      default: '#ffffff',   // Pure white like landing page
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',   // Dark text matching landing page
      secondary: '#666',    // Gray text matching landing page
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
    },
    h2: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.1rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '0.75rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.75rem',
    },
    caption: {
      fontSize: '0.7rem',
    },
    button: {
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,  // Matching landing page
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: false,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 600,
          fontSize: '1rem',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1.05rem',
        },
        contained: {
          boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '48px !important',
          '@media (min-width: 600px)': {
            minHeight: '48px !important',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          borderRadius: '6px',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        },
        elevation2: {
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          borderRadius: '16px',
          border: '1px solid #e5e5e5',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          height: '22px',
          fontSize: '0.7rem',
        },
        sizeSmall: {
          height: '20px',
          fontSize: '0.65rem',
        },
        colorPrimary: {
          backgroundColor: '#4caf50',
          color: '#ffffff',
        },
        colorSecondary: {
          backgroundColor: '#66bb6a',
          color: '#ffffff',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
          },
        },
        sizeSmall: {
          width: 48,
          height: 48,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#4caf50',
          height: 3,
        },
        track: {
          backgroundColor: '#4caf50',
          height: 3,
        },
        rail: {
          backgroundColor: '#c8e6c9',
          height: 3,
        },
        thumb: {
          backgroundColor: '#4caf50',
          width: 16,
          height: 16,
          '&:hover': {
            boxShadow: '0 0 0 8px rgba(76, 175, 80, 0.16)',
          },
        },
        markLabel: {
          fontSize: '0.65rem',
        },
        valueLabel: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#81c784',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4caf50',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontSize: '0.875rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
          },
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#81c784',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4caf50',
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        sizeSmall: {
          fontSize: '0.75rem',
          '& input': {
            padding: '8px 12px',
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:before': {
            backgroundColor: 'rgba(76, 175, 80, 0.12)',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 36,
          '&.Mui-expanded': {
            minHeight: 36,
          },
        },
        content: {
          margin: '8px 0',
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          padding: '12px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: '6px 12px',
          fontSize: '0.75rem',
        },
        icon: {
          fontSize: '1.25rem',
        },
        standardInfo: {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '6px 16px',
          minHeight: 'auto',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '6px 12px',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 32,
          height: 32,
          fontSize: '0.875rem',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            fontSize: '0.75rem',
            minWidth: 28,
            height: 28,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          lineHeight: 1.4,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: 6,
        },
        sizeSmall: {
          padding: 4,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        marginNormal: {
          marginTop: 8,
          marginBottom: 8,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(76, 175, 80, 0.12)',
        },
      },
    },
  },
});